import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useMemo,
  useState,
  type ComponentType,
  type SVGProps,
} from "react";
import {
  FiAward,
  FiEdit3,
  FiEye,
  FiPlus,
  FiMail,
  FiPhone,
  FiShield,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { PhoneInput } from "react-international-phone";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import EventActionButton from "../../../shared/components/EventActionButton";
import ImageUploadField from "../../../shared/components/ImageUploadField";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import PageSizeSelector, {
  resolvePageSizeLimit,
  useStoredPageSize,
} from "../../../shared/components/PageSizeSelector";
import PasswordInput from "../../../shared/components/PasswordInput";
import SearchInput from "../../../shared/components/SearchInput";
import Spinner from "../../../shared/components/Spinner";
import UserAvatar from "../../../shared/components/UserAvatar";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatCpf } from "../../../shared/utils/cpf";
import {
  AuthUser,
  CreateUserPayload,
  UpdateUserPayload,
  USER_CATEGORY_VALUES,
  userCategoryLabel,
} from "../../auth/shared/types/auth";
import type { AsaasOrganizerSettings } from "../services/asaasAdminService";
import AdminCreateUserModal from "./AdminCreateUserModal";

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const AwardIcon = FiAward as unknown as IconLike;
const EditIcon = FiEdit3 as unknown as IconLike;
const EyeIcon = FiEye as unknown as IconLike;
const MailIcon = FiMail as unknown as IconLike;
const PhoneIcon = FiPhone as unknown as IconLike;
const PlusIcon = FiPlus as unknown as IconLike;
const ShieldIcon = FiShield as unknown as IconLike;
const UserIcon = FiUser as unknown as IconLike;
const UsersIcon = FiUsers as unknown as IconLike;

type UserDraft = UpdateUserPayload & { password: string };
type UserQuickFilter =
  | "all"
  | "male"
  | "female"
  | "organizers"
  | "admins"
  | "users";

type AdminUsersTabProps = {
  isAdmin: boolean;
  isOrganizer: boolean;
  users: AuthUser[];
  filteredUsers: AuthUser[];
  usersLoading: boolean;
  userSearch: string;
  setUserSearch: (value: string) => void;
  userDrafts: Record<number, UserDraft>;
  setUserDrafts: Dispatch<SetStateAction<Record<number, UserDraft>>>;
  buildUserDraft: (user: AuthUser) => UserDraft;
  authUserId?: number | null;
  onSaveUser: (userId: number) => Promise<boolean>;
  onDeleteUser: (userId: number) => void;
  onOpenCreateUserModal: () => void;
  showCreateUserModal: boolean;
  onDismissCreateUserModal: () => void;
  newUser: CreateUserPayload;
  setNewUser: Dispatch<SetStateAction<CreateUserPayload>>;
  onCreateUser: (event: FormEvent<HTMLFormElement>) => void;
  onCancelCreateUser: () => void;
  asaasOrganizers: AsaasOrganizerSettings[];
  onSaveOrganizerWallet: (
    organizerId: number,
    walletId: string,
    splitEnabled: boolean,
  ) => Promise<void>;
  paymentsLoading: boolean;
  setAsaasOrganizers: Dispatch<SetStateAction<AsaasOrganizerSettings[]>>;
};

function sexLabel(sex: AuthUser["sex"]) {
  if (sex === "female") return siteTexts.common.sex.female;
  if (sex === "male") return siteTexts.common.sex.male;
  if (sex === "other") return siteTexts.common.sex.other;
  return siteTexts.common.sex.notInformed;
}

function roleMeta(role: AuthUser["role"]) {
  if (role === "admin") {
    return {
      className: "admin-users-role-badge--admin",
      icon: <ShieldIcon aria-hidden="true" />,
      label: "Administrador",
    };
  }
  if (role === "organizer") {
    return {
      className: "admin-users-role-badge--organizer",
      icon: <AwardIcon aria-hidden="true" />,
      label: "Organizador",
    };
  }
  return {
    className: "admin-users-role-badge--user",
    icon: <UserIcon aria-hidden="true" />,
    label: "Usuário",
  };
}

function categoryLabel(category: AuthUser["category"]) {
  return category
    ? userCategoryLabel(category)
    : siteTexts.common.category.notInformed;
}

function userMatchesQuickFilter(user: AuthUser, filter: UserQuickFilter) {
  if (filter === "male") return user.sex === "male";
  if (filter === "female") return user.sex === "female";
  if (filter === "organizers") return user.role === "organizer";
  if (filter === "admins") return user.role === "admin";
  if (filter === "users") return user.role === "user";
  return true;
}

export default function AdminUsersTab({
  isAdmin,
  isOrganizer,
  users,
  filteredUsers,
  usersLoading,
  userSearch,
  setUserSearch,
  userDrafts,
  setUserDrafts,
  buildUserDraft,
  authUserId,
  onSaveUser,
  onDeleteUser,
  onOpenCreateUserModal,
  showCreateUserModal,
  onDismissCreateUserModal,
  newUser,
  setNewUser,
  onCreateUser,
  onCancelCreateUser,
  asaasOrganizers,
  onSaveOrganizerWallet,
  paymentsLoading,
  setAsaasOrganizers,
}: AdminUsersTabProps) {
  const t = siteTexts.admin;
  const info = siteTexts.common.info;
  const [quickFilter, setQuickFilter] = useState<UserQuickFilter>("all");
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  useBodyScrollLock(Boolean(editingUserId));
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useStoredPageSize("tnp_page_size_usuarios");

  const summaryCards = useMemo(
    () => [
      {
        icon: <UserIcon aria-hidden="true" />,
        label: "Total de usuários",
        value: users.length,
      },
      {
        icon: <span aria-hidden="true">♂</span>,
        label: "Masculino",
        value: users.filter((user) => user.sex === "male").length,
      },
      {
        icon: <span aria-hidden="true">♀</span>,
        label: "Feminino",
        value: users.filter((user) => user.sex === "female").length,
      },
      {
        icon: <AwardIcon aria-hidden="true" />,
        label: "Organizadores",
        value: users.filter((user) => user.role === "organizer").length,
      },
    ],
    [users],
  );

  const quickFilters = useMemo(
    () => [
      { value: "all" as const, label: "Todos" },
      { value: "male" as const, label: "Masculino" },
      { value: "female" as const, label: "Feminino" },
      { value: "organizers" as const, label: "Organizadores" },
      { value: "admins" as const, label: "Administradores" },
      { value: "users" as const, label: "Usuários" },
    ],
    [],
  );

  const visibleUsers = useMemo(
    () =>
      filteredUsers.filter((user) => userMatchesQuickFilter(user, quickFilter)),
    [filteredUsers, quickFilter],
  );
  const pageLimit = resolvePageSizeLimit(pageSize, visibleUsers.length);
  const pageCount = Math.max(1, Math.ceil(visibleUsers.length / pageLimit));
  const safePage = Math.min(currentPage, pageCount);
  const pagedUsers =
    pageSize === "all"
      ? visibleUsers
      : visibleUsers.slice((safePage - 1) * pageLimit, safePage * pageLimit);
  const editingUser = users.find((user) => user.id === editingUserId) || null;
  const editingDraft = editingUser
    ? (userDrafts[editingUser.id] ?? buildUserDraft(editingUser))
    : null;
  const editingOrganizerWallet =
    editingUser?.role === "organizer"
      ? asaasOrganizers.find((item) => Number(item.id) === Number(editingUser.id)) ||
        null
      : null;
  const visibleStart =
    visibleUsers.length === 0 ? 0 : (safePage - 1) * pageLimit + 1;
  const visibleEnd = Math.min(safePage * pageLimit, visibleUsers.length);

  const updateDraft = (user: AuthUser, patch: Partial<UserDraft>) => {
    setUserDrafts((prev) => ({
      ...prev,
      [user.id]: {
        ...(prev[user.id] ?? buildUserDraft(user)),
        ...patch,
      },
    }));
  };

  return (
    <div className="admin-content">
      <AdminSection className="admin-users-dashboard">
        <div className="admin-users-hero">
          <div className="admin-users-hero-copy">
            <span className="admin-users-hero-icon" aria-hidden="true">
              <UsersIcon />
            </span>
            <div>
              <h2 className="info-label">
                Usuários
                <InfoTooltip content={info.users} />
              </h2>
              <p>
                Gerencie atletas, organizadores e administradores da plataforma.
              </p>
            </div>
          </div>
          <EventActionButton
            variant="primary"
            className="admin-history-create-button admin-tournaments-create-button"
            onClick={() => {
              if (isOrganizer) {
                setNewUser((prev) => ({ ...prev, role: "user" }));
              }
              onOpenCreateUserModal();
            }}
            icon={<PlusIcon />}
            label={t.actions.addUser}
          />
        </div>

        <div
          className="admin-users-summary-grid"
          aria-label="Resumo de usuários"
        >
          {summaryCards.map((card) => (
            <div className="admin-users-summary-card" key={card.label}>
              <span className="admin-users-summary-icon">{card.icon}</span>
              <div>
                <strong>{card.value}</strong>
                <span>{card.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-users-toolbar">
          <SearchInput
            className="admin-users-search"
            placeholder="Buscar atleta..."
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
            onClear={() => setUserSearch("")}
            aria-label="Buscar por nome, CPF ou e-mail"
          />
          <div className="admin-users-filter-row" aria-label="Filtros rápidos">
            {quickFilters.map((filter) => (
              <button
                key={filter.value}
                className={`admin-users-filter-chip${
                  quickFilter === filter.value ? " is-active" : ""
                }`}
                type="button"
                onClick={() => {
                  setQuickFilter(filter.value);
                  setCurrentPage(1);
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {usersLoading && <Spinner label={t.statuses.loadingUsers} />}

        <div className="admin-users-grid">
          {pagedUsers.map((user) => {
            const role = roleMeta(user.role);

            return (
              <article
                className={`admin-user-card admin-user-card--category-${user.category || "none"}`}
                key={user.id}
              >
                <div className="admin-user-card-top">
                  <UserAvatar
                    avatarUrl={user.avatarUrl}
                    className="admin-user-avatar"
                    name={user.name}
                    sex={user.sex}
                    size={74}
                  />
                  <div className="admin-user-identity">
                    <h3>{user.name || "Atleta TNP"}</h3>
                    <p className="info-label">
                      {categoryLabel(user.category)}
                      <InfoTooltip content={info.userCategory} />
                    </p>
                  </div>
                  <span className={`admin-users-role-badge ${role.className}`}>
                    {role.icon}
                    {role.label}
                  </span>
                </div>

                <div className="admin-user-info-list">
                  <div>
                    <PhoneIcon aria-hidden="true" />
                    <span>{user.phone || "Telefone não informado"}</span>
                  </div>
                  <div>
                    <MailIcon aria-hidden="true" />
                    <span>{user.email || "E-mail não informado"}</span>
                  </div>
                  <div>
                    <span aria-hidden="true">⚧</span>
                    <span>{sexLabel(user.sex)}</span>
                  </div>
                  <div>
                    <span aria-hidden="true">🏆</span>
                    <span>{categoryLabel(user.category)}</span>
                  </div>
                </div>

                <div className="admin-user-card-actions">
                  <a
                    className="admin-user-action admin-user-action--primary"
                    href={`#/jogadores/${user.id}`}
                  >
                    <EyeIcon aria-hidden="true" />
                    Ver perfil
                  </a>
                  {isAdmin && (
                    <button
                      className="admin-user-action"
                      type="button"
                      onClick={() => setEditingUserId(user.id)}
                    >
                      <EditIcon aria-hidden="true" />
                      Editar
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {!usersLoading && visibleUsers.length === 0 && (
          <EmptyState
            className="admin-empty-state"
            icon={<UsersIcon />}
            title={t.usersTab.noUsersFound}
            description={t.misc.noUsersHint}
          />
        )}

        {!usersLoading && visibleUsers.length > 0 && (
          <div className="admin-history-list-footer admin-users-list-footer">
            <span>
              {t.historyList.showing} {visibleStart} {t.historyList.rangeTo}{" "}
              {visibleEnd} {t.historyList.rangeOf} {visibleUsers.length}{" "}
              usuários
            </span>
            <div
              className="admin-history-pagination"
              aria-label={t.historyList.paginationAria}
            >
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                aria-label={t.historyList.previousPageAria}
              >
                ‹
              </button>
              {Array.from({ length: Math.min(3, pageCount) }).map(
                (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      type="button"
                      key={`admin-user-page-${page}`}
                      className={safePage === page ? "active" : ""}
                      aria-current={safePage === page ? "page" : undefined}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                },
              )}
              <button
                type="button"
                disabled={safePage >= pageCount}
                onClick={() =>
                  setCurrentPage((page) => Math.min(pageCount, page + 1))
                }
                aria-label={t.historyList.nextPageAria}
              >
                ›
              </button>
            </div>
            <PageSizeSelector
              className="admin-history-page-size"
              value={pageSize}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1);
              }}
            />
          </div>
        )}

        {!isAdmin && (
          <p className="status-message">{t.statuses.organizerUsersHint}</p>
        )}
      </AdminSection>

      {editingUser && editingDraft && (
        <div
          className="admin-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={`Editar ${editingUser.name || "usuário"}`}
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            setEditingUserId(null);
          }}
        >
          <div className="admin-modal admin-user-edit-modal">
            <div className="admin-modal-header">
              <h3>Editar usuário</h3>
              <Button
                type="button"
                variant="pill"
                onClick={() => setEditingUserId(null)}
              >
                Fechar
              </Button>
            </div>
            <form
              className="admin-user-edit-panel"
              onSubmit={async (event) => {
                event.preventDefault();
                const saved = await onSaveUser(editingUser.id);
                if (saved) setEditingUserId(null);
              }}
            >
              <div className="admin-user-avatar-editor">
                <UserAvatar
                  avatarUrl={editingDraft.avatarUrl || editingUser.avatarUrl}
                  className="admin-user-avatar admin-user-avatar--editor"
                  fallback="initials"
                  name={editingDraft.name || editingUser.name}
                  sex={editingDraft.sex || editingUser.sex}
                  size={92}
                />
                <ImageUploadField
                  aspectRatio={1}
                  label="Foto do usuário"
                  maxFileBytes={2 * 1024 * 1024}
                  outputWidth={720}
                  previewCircle
                  value={editingDraft.avatarUrl || ""}
                  onChange={(nextValue) =>
                    updateDraft(editingUser, { avatarUrl: nextValue })
                  }
                  onRemove={() => updateDraft(editingUser, { avatarUrl: "" })}
                  renderTrigger={({ openFilePicker }) => (
                    <button
                      type="button"
                      className="admin-user-action admin-user-avatar-edit-button"
                      onClick={openFilePicker}
                    >
                      <EditIcon aria-hidden="true" />
                      Editar foto
                    </button>
                  )}
                />
              </div>
              <label className="field">
                <span>{t.labels.name}</span>
                <input
                  type="text"
                  value={editingDraft.name}
                  onChange={(event) =>
                    updateDraft(editingUser, { name: event.target.value })
                  }
                />
              </label>
              <label className="field">
                <span>{t.labels.email}</span>
                <input
                  type="email"
                  value={editingDraft.email}
                  onChange={(event) =>
                    updateDraft(editingUser, { email: event.target.value })
                  }
                />
              </label>
              <label className="field">
                <span>{t.labels.cpf}</span>
                <input
                  type="text"
                  value={editingDraft.cpf}
                  onChange={(event) =>
                    updateDraft(editingUser, {
                      cpf: formatCpf(event.target.value),
                    })
                  }
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  maxLength={14}
                />
              </label>
              <label className="field">
                <span>{t.labels.phone}</span>
                <PhoneInput
                  defaultCountry="br"
                  value={editingDraft.phone}
                  onChange={(value) =>
                    updateDraft(editingUser, { phone: value })
                  }
                  placeholder="+55 (11) 99999-9999"
                />
              </label>
              <label className="field">
                <span>{t.labels.sex}</span>
                <select
                  value={editingDraft.sex}
                  onChange={(event) =>
                    updateDraft(editingUser, {
                      sex: event.target.value as UpdateUserPayload["sex"],
                    })
                  }
                >
                  <option value="">{siteTexts.common.sex.notInformed}</option>
                  <option value="female">{siteTexts.common.sex.female}</option>
                  <option value="male">{siteTexts.common.sex.male}</option>
                  <option value="other">{siteTexts.common.sex.other}</option>
                </select>
              </label>
              <label className="field">
                <span className="info-label">
                  {t.labels.category}
                  <InfoTooltip content={info.userCategory} />
                </span>
                <select
                  value={editingDraft.category}
                  onChange={(event) =>
                    updateDraft(editingUser, {
                      category: event.target
                        .value as UpdateUserPayload["category"],
                    })
                  }
                >
                  {USER_CATEGORY_VALUES.map((item) => (
                    <option
                      key={`admin-user-category-${editingUser.id}-${item || "empty"}`}
                      value={item}
                    >
                      {item
                        ? userCategoryLabel(item)
                        : siteTexts.common.category.notInformed}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="info-label">
                  {t.labels.profile}
                  <InfoTooltip content={info.userRole} />
                </span>
                <select
                  value={editingDraft.role}
                  disabled={Number(authUserId || 0) === Number(editingUser.id)}
                  onChange={(event) =>
                    updateDraft(editingUser, {
                      role: event.target.value as UpdateUserPayload["role"],
                    })
                  }
                >
                  <option value="user">{t.statuses.user}</option>
                  <option value="organizer">{t.statuses.organizer}</option>
                  <option value="admin">{t.statuses.administrator}</option>
                </select>
                {Number(authUserId || 0) === Number(editingUser.id) && (
                  <small>{t.usersTab.selfPermissionHint}</small>
                )}
              </label>
              <label className="field">
                <span>{t.labels.newPasswordOptional}</span>
                <PasswordInput
                  value={editingDraft.password}
                  onChange={(event) =>
                    updateDraft(editingUser, { password: event.target.value })
                  }
                />
              </label>

              {editingUser.role === "organizer" && editingOrganizerWallet && (
                <fieldset className="admin-user-wallet-panel">
                  <legend>{t.paymentsTab.myWallet}</legend>
                  <label className="field">
                    <span>{t.paymentsTab.walletId}</span>
                    <input
                      type="text"
                      value={editingOrganizerWallet.walletId || ""}
                      onChange={(event) =>
                        setAsaasOrganizers((prev) =>
                          prev.map((item) =>
                            item.id === editingUser.id
                              ? { ...item, walletId: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                  </label>
                  <label className="field">
                    <span className="info-label">
                      {t.paymentsTab.organizerSplit}
                      <InfoTooltip content={info.split} />
                    </span>
                    <select
                      value={editingOrganizerWallet.splitEnabled ? "true" : "false"}
                      onChange={(event) =>
                        setAsaasOrganizers((prev) =>
                          prev.map((item) =>
                            item.id === editingUser.id
                              ? {
                                  ...item,
                                  splitEnabled: event.target.value === "true",
                                }
                              : item,
                          ),
                        )
                      }
                    >
                      <option value="true">{t.paymentsTab.active}</option>
                      <option value="false">{t.paymentsTab.inactive}</option>
                    </select>
                  </label>
                  <div className="admin-user-wallet-actions">
                    <Button
                      type="button"
                      variant="pill"
                      disabled={paymentsLoading}
                      onClick={() =>
                        onSaveOrganizerWallet(
                          editingUser.id,
                          editingOrganizerWallet.walletId || "",
                          Boolean(editingOrganizerWallet.splitEnabled),
                        )
                      }
                    >
                      {t.paymentsTab.saveWallet}
                    </Button>
                  </div>
                </fieldset>
              )}

              <div className="admin-modal-actions">
                <Button
                  type="button"
                  variant="pill"
                  onClick={() => setEditingUserId(null)}
                >
                  {siteTexts.common.cancel}
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => onDeleteUser(editingUser.id)}
                >
                  {siteTexts.common.remove}
                </Button>
                <Button type="submit" variant="primary">
                  {siteTexts.common.save}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateUserModal && (
        <AdminCreateUserModal
          isAdmin={isAdmin}
          newUser={newUser}
          onCancel={onCancelCreateUser}
          onDismiss={onDismissCreateUserModal}
          onSubmit={onCreateUser}
          setNewUser={setNewUser}
        />
      )}
    </div>
  );
}
